import {
	OrderFieldTypeEnum,
	type OrderSetupDetailResponseDto,
	OrderSetupStatusEnum,
} from "Infrastructure/Api/Api";
import { UserPermissions } from "Infrastructure/Api/permissions";
import { Resources, useResource } from "Infrastructure/Translation/Resources";
import { useHandleServerError } from "Infrastructure/Utilities/Forms/Hooks/useHandleServerError";
import { createSelectOptions } from "Infrastructure/Utilities/Helpers/createSelectOptions";
import { hasPermission } from "Infrastructure/Utilities/Permissions/hasPermission";
import { useUpdateOrderSetupMutation } from "Settings/Modules/OrderSetup/Api/Mutation/useUpdateOrderSetupMutation";
import {
	getOrderSetupEditFormSchema,
	type OrderSetupEditFormModel,
} from "Settings/Modules/OrderSetup/Components/Form/Schema/orderSetupEditFormSchema";
import { SortableItem } from "Settings/Modules/OrderSetup/Components/SortableItem";
import Input from "Shared/Components/Form/CustomFormComponent/Input";
import Select from "Shared/Components/Form/CustomFormComponent/Select";
import { closestCenter, DndContext, type DragEndEvent } from "@dnd-kit/core";
import {
	SortableContext,
	verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { zodResolver } from "@hookform/resolvers/zod";
import { Add, Save } from "@mui/icons-material";
import {
	Box,
	Button,
	FormHelperText,
	Grid2,
	Stack,
	Typography,
} from "@mui/material";
import { useEffect, useMemo } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { toast } from "sonner";

type OrderSetupEditFormProps = {
	orderSetup: OrderSetupDetailResponseDto;
};

const formResource = Resources.base.orderSetup.editForm;

export function OrderSetupEditForm({ orderSetup }: OrderSetupEditFormProps) {
	const { t } = useResource();
	const formDefaultValues = useMemo(
		() => ({
			name: orderSetup.name,
			status: orderSetup.status ?? null,
			orderFields: orderSetup.orderFields.map((field) => ({
				type: field.type,
				key: field.key,
				label: field.label,
				value: field.value,
				orderIndex: field.orderIndex,
				emailLabel: field.emailLabel ?? "",
				isEmailIncluded: field.isEmailIncluded ?? true,
			})),
		}),
		[orderSetup.id, orderSetup.name, orderSetup.status, orderSetup.orderFields],
	);

	const form = useForm<OrderSetupEditFormModel>({
		resolver: zodResolver(getOrderSetupEditFormSchema(t)),
		defaultValues: formDefaultValues,
	});

	const {
		control,
		formState: { errors },
		setValue,
		getValues,
		watch,
	} = form;

	useEffect(() => {
		form.reset(formDefaultValues);
	}, [orderSetup.id]);

	const { mutate: updateOrderSetup, isPending } = useUpdateOrderSetupMutation(
		orderSetup.id,
	);

	const { fields, append, remove, move } = useFieldArray({
		control,
		name: "orderFields",
	});

	const { handleServerError } = useHandleServerError(form);

	const handleOrderSetupUpdate = () => {
		handleServerError((data: OrderSetupEditFormModel) =>
			updateOrderSetup(data),
		);
	};

	const handleDeploy = () => {
		if (!orderSetup.isLocked) {
			setValue("status", OrderSetupStatusEnum.deployed);
			handleOrderSetupUpdate();
			return;
		}

		updateOrderSetup({
			status: OrderSetupStatusEnum.deployed,
		});
	};

	const disableUpdateButton = !hasPermission(
		UserPermissions.OrderSetupModule.update,
	);

	const formDisabled =
		orderSetup.status === OrderSetupStatusEnum.deployed ||
		isPending ||
		orderSetup.isLocked;

	const handleDragEnd = (event: DragEndEvent) => {
		const { active, over } = event;
		if (!over || active.id === over.id) return;

		const oldIndex = fields.findIndex((field) => field.id === active.id);
		const newIndex = fields.findIndex((field) => field.id === over.id);

		move(oldIndex, newIndex);

		const updatedFields = getValues("orderFields").map((field, index) => ({
			...field,
			orderIndex: index,
		})) as OrderSetupEditFormModel["orderFields"];

		setValue("orderFields", updatedFields);
	};

	const statusSelectOptions =
		orderSetup.status === OrderSetupStatusEnum.deployed
			? createSelectOptions(OrderSetupStatusEnum)
			: createSelectOptions(OrderSetupStatusEnum).filter(
					(option) => option.value !== OrderSetupStatusEnum.deployed,
				);

	return (
		<Stack overflow="auto">
			<Grid2 container spacing={2}>
				<Grid2 size={{ xs: 12, md: 6 }}>
					<Input
						id="name"
						name="name"
						control={control}
						label={t(formResource.name)}
						errors={errors}
						disabled={formDisabled}
						variant="outlined"
						fullWidth
					/>
				</Grid2>
				<Grid2 size={{ xs: 12, md: 6 }}>
					<Select
						id="status"
						name="status"
						control={control}
						label={t(formResource.status)}
						errors={errors}
						disabled={formDisabled}
						options={statusSelectOptions}
						variant="outlined"
						fullWidth
					/>
				</Grid2>
			</Grid2>

			<Typography variant="h5" mt={3}>
				{t(formResource.orderFields)}
			</Typography>
			{errors.orderFields && (
				<FormHelperText error>{errors.orderFields.message}</FormHelperText>
			)}

			<DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
				<SortableContext
					items={fields.map((field) => field.id)}
					strategy={verticalListSortingStrategy}
				>
					<Stack rowGap={2}>
						{[...fields]
							.sort((a, b) => a.orderIndex - b.orderIndex)
							.map((field, index) => (
								<SortableItem
									key={field.id}
									id={field.id}
									index={index}
									remove={remove}
									control={control}
									errors={errors}
									watch={watch}
									getValues={getValues}
									setValue={setValue}
									disabledUpdate={disableUpdateButton || formDisabled}
								/>
							))}
					</Stack>
				</SortableContext>
			</DndContext>

			<Stack flexDirection="row" mt={4} gap={2}>
				{!orderSetup.isLocked && (
					<Box>
						<Button
							variant="outlined"
							startIcon={<Add />}
							onClick={() =>
								append({
									key: "",
									label: "",
									type: OrderFieldTypeEnum.string,
									orderIndex: fields.length ?? 0,
									emailLabel: "",
									isEmailIncluded: true,
								})
							}
							disabled={formDisabled}
						>
							{t(formResource.addOrderField)}
						</Button>
					</Box>
				)}

				{!orderSetup.isLocked && (
					<Box>
						<Button
							variant="contained"
							startIcon={<Save />}
							onClick={handleOrderSetupUpdate}
							loading={isPending}
							disabled={disableUpdateButton || formDisabled}
						>
							{t(formResource.saveSetup)}
						</Button>
					</Box>
				)}
				<Stack direction="row" columnGap={2}>
					<Button
						variant={
							orderSetup.status === OrderSetupStatusEnum.deployed
								? "outlined"
								: "contained"
						}
						color="success"
						startIcon={<Save />}
						onClick={
							orderSetup.status === OrderSetupStatusEnum.deployed
								? () => {
										toast.warning("This version is currently deployed");
									}
								: handleDeploy
						}
						loading={isPending}
						disabled={disableUpdateButton}
					>
						{orderSetup.status === OrderSetupStatusEnum.deployed
							? t(formResource.deployedStatus)
							: t(formResource.deploySetup)}
					</Button>
				</Stack>
			</Stack>
		</Stack>
	);
}
